import { Injectable, inject } from '@angular/core';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import {
  ColDef,
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  SortModelItem,
} from 'ag-grid-community';
import { Subject, map } from 'rxjs';
import { GridContext } from '../interfaces/grid-context';
import GridUtils from '../utils/grid.utils';

@Injectable({ providedIn: 'root' })
export class GridDatasourceService {
  private restApiService = inject(RestApiService);

  createServerSideDataSource(
    entity: string,
    gridDataLoaded$?: Subject<number>,
    sortModel?: SortModelItem[]
  ): IServerSideDatasource {
    return {
      getRows: (
        params: IServerSideGetRowsParams<unknown, GridContext>
      ): void => {
        // Build the request body of the API request based on the grid
        const request = params.request as GetRowsRequest &
          IServerSideGetRowsRequest;
        const context = params.context;

        if (!context.isGridPreloadCompleted) return;

        if (context.extraFilter) {
          request.filterModel = {
            ...request.filterModel,
            ...context.extraFilter,
          };
        }

        if (context.customFilter) {
          request.customFilter = context.customFilter;
        }

        if (context.sum) {
          request.sum = context.sum;
        }

        // Get the fields based on the grid columns
        const fields = params.api
          .getColumns()
          ?.filter(column => !column.isRowGroupActive())
          .map(column => column.getColDef())
          .filter(colDef => !colDef.aggFunc)
          .map(o => o.field)
          .filter(f => f) as string[];
        // Create the API request body
        request.entity = entity;
        request.fields = fields;
        request.quickFilterText = context.quickFilterText;
        if (sortModel) {
          request.sortModel = sortModel;
        }
        this.restApiService.getData(request).subscribe(res => {
          const { data, totalCount } = res;
          context.totalCount = totalCount;
          if (res.sum) {
            context.pinnedBottomRowData = [res.sum];
          }
          params.success({
            rowData: data,
          });
          // make sure new data is laoded in the grid
          setTimeout(() => {
            gridDataLoaded$?.next(data?.length);
          });
        });
      },
    };
  }

  setTreeRowData = <T>(
    api: GridApi,
    context: GridContext,
    rowIdToSelect?: string
  ): void => {
    const { extraFilter, treeDataConfig } = context;
    if (!treeDataConfig) {
      console.error('Missing context.treeDataConfig');
      return;
    }
    api.setGridOption('loading', true);
    const entity = context.entity!;
    const fields =
      api
        .getColumnDefs()
        ?.filter((colDef: ColDef) => !!colDef.field)
        .map((colDef: ColDef) => colDef.field!) || [];

    this.restApiService
      .getData<T>({
        entity,
        fields,
        filterModel: { ...api.getFilterModel(), ...extraFilter },
      })
      .pipe(map(resp => resp.data))
      .subscribe(data => {
        const rowData = GridUtils.getDataWithTreePath(data, treeDataConfig);
        api.clearCellSelection();
        api.setGridOption('rowData', rowData);
        api.setGridOption('loading', false);

        if (rowIdToSelect) {
          const nodeToSelect = api.getRowNode(rowIdToSelect);
          nodeToSelect?.setSelected(true, true);
          api.ensureNodeVisible(nodeToSelect);
        }
      });
  };
}
